import { useState, useEffect } from 'react';
import { useParams, useLocation, Link, useSearchParams } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Grid,
  Button,
  Alert,
  Loader,
  Stack
} from '@mantine/core';
import { IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import { AddressService, CombinedPackageService, PackageService, ShipmentService } from '../types';
import { Address, PackageToShip, ShipmentDetails } from '../types/shipping';
import AddressSection from '../components/shipment/AddressSection';
import PackageSummary from '../components/shipment/PackageSummary';
import SuccessModal from '../components/shipment/SuccessModal';
import { notifications } from '@mantine/notifications';

const Shipping = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const packageId = queryParams.get('packageId');
  const [searchParams] = useSearchParams();
  const packageType = searchParams.get('type') || 'received';

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [packages, setPackages] = useState<PackageToShip[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isShippingConfirmed, setIsShippingConfirmed] = useState(false);
  const [shipmentDetails, setShipmentDetails] = useState<ShipmentDetails | null>(null);


  useEffect(() => {
    const fetchShippingData = async () => {
      setIsLoading(true);
      try {
        // Fetch addresses from API
        const addressesResponse = await fetchAdresses();

        // Transform addresses to match our interface
        const transformedAddresses: any[] = addressesResponse.map((addr: any) => ({
          id: String(addr.id),
          name: addr.user_id || 'Default',
          street: addr.address || '',
          city: addr.city || '',
          state: addr.state || '',
          postalCode: addr.zip || '',
          country: addr.country || '',
        }));

        let packagesData: any[] = [];

        // If packageId exists, fetch the specific package
        if (packageId) {
          if (packageType == 'received') {
            const packageResponse: any = await PackageService.getPackage1(parseInt(packageId, 10));

            // Calculate total value from items if available
            const totalValue = packageResponse.item?.reduce((sum: number, item: any) => {
              return sum + (Number(item.price) * Number(item.quantity || 1));
            }, 0) || Number(packageResponse.custom_value) || 0;

            // Transform API response to match PackageToShip interface
            packagesData = [{
              id: String(packageResponse.id),
              name: packageResponse.description || 'Package',
              weight: Number(packageResponse.weight) || 0,
              dimensions: {
                length: Number(packageResponse.length) || 0,
                width: Number(packageResponse.width) || 0,
                height: Number(packageResponse.height) || 0,
              },
              value: totalValue,
              shipping_cost: Number(packageResponse.shipping_cost) || 0
            }];
          } else if (packageType == 'consolidated') {
            // For consolidated shipments, we can use the packageId to fetch multiple packages
            const consolidatedResponse: any = await CombinedPackageService.getPackageCombine1(Number(packageId));

            packagesData = consolidatedResponse.package.map((pkg: any) => {
              // Calculate total value for each package from items
              const totalValue = pkg.item?.reduce((sum: number, item: any) => {
                return sum + (Number(item.price) * Number(item.quantity || 1));
              }, 0) || 0;

              return {
                id: String(pkg.id),
                name: pkg.description || 'Package',
                weight: Number(pkg.weight) || 0,
                dimensions: {
                  length: Number(pkg.length) || 0,
                  width: Number(pkg.width) || 0,
                  height: Number(pkg.height) || 0,
                },
                value: totalValue,
                shipping_cost: Number(pkg.shipping_cost) || 0
              };
            });

          }
        } else {
          throw new Error('No package or shipment ID provided');
        }

        // Set default selections
        const defaultAddress = transformedAddresses[0];
        setSelectedAddressId(defaultAddress?.id || '');

        setAddresses(transformedAddresses);
        setPackages(packagesData);

      } catch (err) {
        setError('Failed to load shipping data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchShippingData();
  }, [packageId, id]);

  const fetchAdresses = async () => {
    return AddressService.getAddress();
  }
  const handleAddressChange = (value: string | null) => {
    if (value !== null) {
      setSelectedAddressId(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that an address is selected
    if (!selectedAddressId) {
      notifications.show({
        title: 'Missing Information',
        message: 'Please select a shipping address',
        color: 'yellow',
      });
      return;
    }

    setIsProcessing(true);

    try {
      // Determine which ID to use based on package type
      let requestPayload;

      if (packageType === 'consolidated') {
        requestPayload = {
          AddressId: Number(selectedAddressId),
          CombPackageId: packageId ? Number(packageId) : undefined,
        };
      } else {
        requestPayload = {
          AddressId: Number(selectedAddressId),
          PackageId: packageId ? Number(packageId) : undefined,
        };
      }

      const response: any = await ShipmentService.postShipment(requestPayload);

      if (!response?.data?.shipment) {
        throw new Error('Invalid response from server');
      }

      const shipment = response.data.shipment;

      setShipmentDetails({
        shipmentId: shipment.id?.toString() || '',
        trackingNumber: shipment.order_id,
        createdAt: shipment.created_at || new Date().toLocaleString(),
        url: response.data.paymentUrl,
        estimatedDelivery: '2-3 business days'
      });

      // Set shipping as confirmed - this will trigger the view change
      setIsShippingConfirmed(true);

    } catch (error) {
      // Show notification using Mantine's notification system
      notifications.show({
        title: 'Shipment Error',
        message: 'Failed to create shipment. Please try again.',
        color: 'red',
      });
      console.error('Shipping error:', error);
    } finally {
      setIsProcessing(false);
    }
  };
  if (isLoading) {
    return (
      <Container py="xl">
        <Stack align="center" my="xl">
          <Loader size="lg" />
          <Text>Loading shipping details...</Text>
        </Stack>
      </Container>
    );
  }

  if (error || !packages.length) {
    return (
      <Container py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          mb="lg"
        >
          {error || 'No packages found to ship'}
        </Alert>
        <Button
          leftSection={<IconArrowLeft size={16} />}
          component={Link}
          to="/packages"
          variant="light"
        >
          Back to Packages
        </Button>
      </Container>
    );
  }

  // If shipping is confirmed, show only the success card
  if (isShippingConfirmed && shipmentDetails) {
    return (
      <Container size="xl" py="xl">
        <SuccessModal shipmentDetails={shipmentDetails} />
      </Container>
    );
  }

  // Otherwise show the normal shipping form
  return (
    <Container size="xl" py="xl">
      <Button
        component={Link}
        to={packageId ? `/packages/${packageId}` : '/packages'}
        leftSection={<IconArrowLeft size={16} />}
        variant="light"
        mb="md"
      >
        Back
      </Button>

      <Title order={1} mb="sm">Ship Your Package{packages.length > 1 ? 's' : ''}</Title>
      <Text c="dimmed" mb="xl">Complete your shipping details and confirm payment.</Text>

      {/* Only render modal when not confirmed yet */}
      {!isShippingConfirmed && (
        <form onSubmit={handleSubmit}>
          <Grid gutter="lg">
            <Grid.Col span={{ base: 12, md: 8 }}>
              <AddressSection
                addresses={addresses}
                selectedAddressId={selectedAddressId}
                onAddressChange={handleAddressChange}
                setAddresses={setAddresses}
                isLoading={isLoading}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 4 }}>
              <PackageSummary
                packages={packages}
                packageId={packageId}
                isProcessing={isProcessing}
              />
            </Grid.Col>
          </Grid>
        </form>
      )}
    </Container>
  );
};

export default Shipping;
